import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import countriesGeo from "../data/countries.geojson";
import { POI_COUNTRIES } from "../data/poi";

const HUB = { lat: 16.9891, lng: 82.2475 };

const fmt = (n) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : `${n}`;

export const CoverageGlobe = () => {
  const globeRef = useRef();
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 520, h: 520 });
  const [hovered, setHovered] = useState(null);

  const byIso = useMemo(() => {
    const m = {};
    for (const c of POI_COUNTRIES) m[c.iso3] = c;
    return m;
  }, []);

  const maxCount = useMemo(() => Math.max(...POI_COUNTRIES.map((c) => c.count)), []);

  const polygons = useMemo(() => countriesGeo.features, []);

  const capColor = (feat) => {
    const iso = feat.properties.iso3;
    const c = byIso[iso];
    if (!c) return "rgba(148,163,184,0.05)";
    if (hovered && hovered.iso3 === iso) return "rgba(0,240,255,0.9)";
    const t = Math.pow(c.count / maxCount, 0.3);
    const alpha = 0.3 + t * 0.55;
    return iso === "IND" ? "rgba(0,240,255,0.85)" : `rgba(37,150,235,${alpha})`;
  };

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    globe.controls().autoRotate = true;
    globe.controls().autoRotateSpeed = 0.5;
    globe.controls().enableZoom = false;
    globe.pointOfView({ lat: 24, lng: 60, altitude: 2.0 }, 1400);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      const s = Math.min(Math.max(width - 8, 280), 560);
      setSize({ w: s, h: s });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full flex items-center justify-center" data-testid="coverage-globe">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[70%] h-[70%] rounded-full bg-cyan-400/10 blur-[90px]" />
      </div>
      <Globe
        ref={globeRef}
        width={size.w}
        height={size.h}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
        polygonsData={polygons}
        polygonCapColor={capColor}
        polygonSideColor={() => "rgba(0,0,0,0)"}
        polygonStrokeColor={() => "rgba(148,163,184,0.15)"}
        polygonAltitude={(feat) => (byIso[feat.properties.iso3] ? 0.012 : 0.002)}
        onPolygonHover={(feat) => {
          const c = feat && byIso[feat.properties.iso3];
          setHovered(c || null);
          const globe = globeRef.current;
          if (globe) globe.controls().autoRotate = !c;
        }}
        ringsData={[HUB]}
        ringLat="lat"
        ringLng="lng"
        ringColor={() => (t) => `rgba(0,240,255,${0.7 * (1 - t)})`}
        ringMaxRadius={5}
        ringPropagationSpeed={1.4}
        ringRepeatPeriod={2200}
        atmosphereColor="#2563EB"
        atmosphereAltitude={0.18}
      />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#05070A]/85 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 pointer-events-none whitespace-nowrap">
        <span className="relative w-1.5 h-1.5 rounded-full bg-cyan-400 ping-dot shrink-0" />
        {hovered ? (
          <span className="font-mono-tech text-[10px] tracking-[0.2em] text-cyan-300 uppercase" data-testid="globe-hover-label">
            {hovered.country} — {fmt(hovered.count)} POIs
          </span>
        ) : (
          <span className="font-mono-tech text-[10px] tracking-[0.2em] text-cyan-300 uppercase">
            51 countries — hover to explore
          </span>
        )}
      </div>
    </div>
  );
};

export default CoverageGlobe;
